import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";
import {
  buildConfiguredPriceDriverCombinationRows,
  buildQualityAttributeValuesByHashMap,
  pivotQualityAttributes,
  toQualityAttributeValueHash,
} from "../../common/services/products/productUtilities";
import { getSpotByTermSchedule } from "../../common/services/markets/termService";
import { getFiniteValuesForKeys, joinArrays } from "../../common/services/utilities";
import MarkCell from "./markCell";
import CandlestickChart from "../../common/components/d3/candlestickChart";
import { useTranslation } from "react-i18next";
import uuid from "react-uuid";
import {
  aggregateMarks,
  aggregateMarksWithoutAttributes,
  calculateAveragePrice,
  getLowestDenominatorMarkState,
} from "../../common/services/marketdata/marketDataUtilities";
import { HistoricalReportingPeriodicity } from "../../common/enumerations/enums";

const buildSpotTermMarketDateKey = ({ marketDate, termId }) =>
  `${marketDate ?? ""}|${termId ?? ""}`;

const averageOrNull = (values = []) =>
  values.length > 0
    ? values.reduce((total, value) => total + value, 0) / values.length
    : null;

const maxOrNull = (values = []) =>
  values.length > 0 ? Math.max(...values) : null;

const minOrNull = (values = []) =>
  values.length > 0 ? Math.min(...values) : null;

const filterMarksToSpotTerms = (marks = [], spotTerms = []) => {
  const spotTermKeys = new Set(
    spotTerms.map((spotTerm) => buildSpotTermMarketDateKey(spotTerm))
  );

  return marks.filter((mark) =>
    spotTermKeys.has(buildSpotTermMarketDateKey(mark))
  );
};

const buildDailyTermsFromMarks = (marks = []) => {
  const termsByMarketDate = marks.reduce((result, mark) => {
    if (!mark.marketDate || result[mark.marketDate]) {
      return result;
    }

    result[mark.marketDate] = {
      marketDate: mark.marketDate,
      termId: mark.termId,
      name: mark.termName,
    };

    return result;
  }, {});

  return Object.values(termsByMarketDate).sort((a, b) =>
    new Date(a.marketDate) > new Date(b.marketDate) ? 1 : -1
  );
};

const aggregateSpotMarksByMarketDate = (marks = []) => {
  const preAggregatedMarks = aggregateMarks(marks);
  const groupedByMarketDateAndAttribute = preAggregatedMarks.reduce(
    (result, mark) => {
      const groupKey = `${mark.marketDate}|${mark.qualityAttributeValueHash}`;

      if (!result[groupKey]) {
        result[groupKey] = [];
      }

      result[groupKey].push(mark);
      return result;
    },
    {}
  );

  return Object.values(groupedByMarketDateAndAttribute).map((group) => {
    const firstMark = group[0];

    return {
      ...firstMark,
      // Normalize spot marks onto the market date so multiple spot terms
      // for a day still render as one historical bucket.
      termId: firstMark.marketDate,
      price: calculateAveragePrice(group),
      high: maxOrNull(getFiniteValuesForKeys(group, ["high"])),
      low: minOrNull(getFiniteValuesForKeys(group, ["low"])),
      open: averageOrNull(getFiniteValuesForKeys(group, ["open"])),
      close: averageOrNull(getFiniteValuesForKeys(group, ["close"])),
      markState: getLowestDenominatorMarkState(group),
    };
  });
};

const getCandlestickBounds = (marks = []) => {
  const aggregatedData = aggregateMarksWithoutAttributes(marks);

  const rawDataValues = getFiniteValuesForKeys(marks, ["price", "bid", "offer"]);
  const aggregatedDataValues = getFiniteValuesForKeys(aggregatedData, [
    "high",
    "low",
    "open",
    "close",
  ]);

  const allValues = [...rawDataValues, ...aggregatedDataValues];
  const yMinRaw = allValues.length > 0 ? Math.min(...allValues) : Number.POSITIVE_INFINITY;
  const yMaxRaw = allValues.length > 0 ? Math.max(...allValues) : Number.NEGATIVE_INFINITY;

  const yMin =
    yMinRaw !== Number.POSITIVE_INFINITY
      ? yMinRaw > 0
        ? yMinRaw * 0.95
        : yMinRaw * 1.05
      : undefined;

  const yMax =
    yMaxRaw !== Number.NEGATIVE_INFINITY
      ? yMaxRaw > 0
        ? yMaxRaw * 1.05
        : yMaxRaw * 0.95
      : undefined;

  return { yMin, yMax };
};

const attachQualityAttributeValues = (mark, qualityAttributeValuesByHash) => {
  const existingQualityAttributeValues = mark?.qualityAttributeValues;
  const hasExistingQualityAttributeValues =
    Array.isArray(existingQualityAttributeValues) &&
    existingQualityAttributeValues.length > 0;

  if (hasExistingQualityAttributeValues) return mark;

  return {
    ...mark,
    qualityAttributeValues:
      qualityAttributeValuesByHash.get(mark?.qualityAttributeValueHash) ?? [],
  };
};

const buildProductCombinationRows = (product) =>
  pivotQualityAttributes(product).map((row) => ({
    ...row,
    qualityAttributeValueHash: toQualityAttributeValueHash(
      row.qualityAttributeValues
    ),
  }));

const MarksHistoryTable = (props) => {
  const { t } = useTranslation(["marketdata"]);
  const termsCacheRef = useRef(new Map());
  const termsRequestIdRef = useRef(0);
  const autoSizeFrameRef = useRef(null);
  const [tableState, setTableState] = useState({
    qualityAttributeValueHash: null,
    startDate: null,
    endDate: null,
    terms: [],
    isLoading: true,
  });

  const {
    marks: propsMarks,
    product,
    fromDate,
    toDate,
    termScheduleId,
    marketPriceDriverCombinations,
    organizationId,
    marketId,
    marketDate,
    historicalReportingPeriodicity,
    priceBasisId,
    currencyId,
    uomId,
    pricePrecision,
    volumePrecision,
    currencySymbol,
  } = props;

  const rowHover = useCallback((data) => {
    setTableState((prevState) =>
      prevState.qualityAttributeValueHash === data.qualityAttributeValueHash
        ? prevState
        : {
            ...prevState,
            qualityAttributeValueHash: data.qualityAttributeValueHash,
          }
    );
  }, []);

  const rowExit = useCallback(() => {
    setTableState((prevState) =>
      prevState.qualityAttributeValueHash === null
        ? prevState
        : {
            ...prevState,
            qualityAttributeValueHash: null,
          }
    );
  }, []);

  const columnDefs = useMemo(() => {
    if (!product || !tableState.startDate) return [];

    const marketDates = [...new Set(tableState.terms.map((term) => term.marketDate))]
      .filter(Boolean)
      .sort((a, b) => (new Date(a) > new Date(b) ? -1 : 1));

    return [
      {
        field: "shortName",
        headerName: t("MarketDataProduct"),
        filter: false,
        sortable: false,
        pinned: "left",
        suppressMovable: true,
      },
      ...product.qualityAttributes
        .filter((qualityAttribute) => qualityAttribute.isPriceDriver !== false)
        .map((qualityAttribute) => ({
          field: qualityAttribute.qualityAttributeId,
          headerName: qualityAttribute.qualityAttributeName,
          filter: false,
          sortable: false,
          pinned: "left",
          suppressMovable: true,
        })),
      ...marketDates.map((spotMarketDate) => ({
          field: spotMarketDate,
          headerName:
            spotMarketDate.slice(0, 10) +
            (historicalReportingPeriodicity === "Hour"
              ? "H" + spotMarketDate.slice(11, 13)
              : ""),
          filter: false,
          sortable: false,
          cellDataType: false,
          cellRenderer: (params) => {
            const data = params.data;
            return (
              <MarkCell
                termId={data[spotMarketDate]?.termId}
                organizationId={organizationId}
                marketId={marketId}
                priceBasisId={priceBasisId}
                currencyId={currencyId}
                uomId={uomId}
                pricePrecision={pricePrecision}
                volumePrecision={volumePrecision}
                currencySymbol={currencySymbol}
                marketDate={marketDate}
                mark={
                  data[spotMarketDate] ?? {
                    markId: uuid(),
                    eventId: uuid(),
                    active: true,
                  }
                }
                qualityAttributeValues={data.qualityAttributeValues}
                shortName={data.shortName}
                isNew={!data[spotMarketDate]}
              />
            );
          },
        })),
    ];
  }, [
    product,
    tableState.terms,
    tableState.startDate,
    t,
    historicalReportingPeriodicity,
    organizationId,
    marketId,
    priceBasisId,
    currencyId,
    uomId,
    pricePrecision,
    volumePrecision,
    currencySymbol,
    marketDate,
  ]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      type: "centerAligned",
      minWidth: 140,
      wrapText: false,
      autoHeight: false,
      wrapHeaderText: false,
      autoHeaderHeight: false,
    }),
    []
  );

  useEffect(() => {
    return () => {
      if (autoSizeFrameRef.current) {
        cancelAnimationFrame(autoSizeFrameRef.current);
      }
    };
  }, []);

  const autoSizeGridColumns = useCallback((params) => {
    if (!params?.api) return;

    if (autoSizeFrameRef.current) {
      cancelAnimationFrame(autoSizeFrameRef.current);
    }

    autoSizeFrameRef.current = requestAnimationFrame(() => {
      params.columnApi?.autoSizeAllColumns?.(false);
      params.api?.autoSizeAllColumns?.(false);
      autoSizeFrameRef.current = null;
    });
  }, []);

  const joinMarks = useCallback((pivotedData, marks) => {
    const groupedByQualityAttributeValueHash = marks.reduce((result, obj) => {
      const {
        qualityAttributeValueHash,
        marketDate: rowMarketDate,
        ...rest
      } = obj;

      if (!result[qualityAttributeValueHash]) {
        result[qualityAttributeValueHash] = {};
      }

      result[qualityAttributeValueHash][rowMarketDate] = {
        ...rest,
        marketDate: rowMarketDate,
      };

      return result;
    }, {});

    const pivotedMarks = Object.entries(groupedByQualityAttributeValueHash).map(
      ([qualityAttributeValueHash, terms]) => ({
        qualityAttributeValueHash,
        ...terms,
      })
    );

    return joinArrays(pivotedData, pivotedMarks, "qualityAttributeValueHash");
  }, []);

  const { marks, candlestickMarksRaw } = useMemo(() => {
    if (!product || !tableState.startDate) {
      return { marks: [], candlestickMarksRaw: [] };
    }

    const filteredSpotMarks = filterMarksToSpotTerms(propsMarks, tableState.terms);
    const aggregatedSpotMarks = aggregateSpotMarksByMarketDate(filteredSpotMarks);
    const configuredCombinationRows = buildConfiguredPriceDriverCombinationRows(
      product,
      marketPriceDriverCombinations
    );
    const combinationRows =
      configuredCombinationRows.length > 0
        ? configuredCombinationRows
        : buildProductCombinationRows(product);
    const qualityAttributeValuesByHash =
      buildQualityAttributeValuesByHashMap(combinationRows);
    const validHashes = new Set(
      combinationRows.map((row) => row.qualityAttributeValueHash)
    );
    const filteredConfiguredSpotMarks = aggregatedSpotMarks.filter((mark) =>
      validHashes.has(mark.qualityAttributeValueHash)
    );
    const joinedData = joinMarks(combinationRows, filteredConfiguredSpotMarks);
    const nextCandlestickMarksRaw = filteredConfiguredSpotMarks
      .map((mark) =>
        attachQualityAttributeValues(mark, qualityAttributeValuesByHash)
      );

    return {
      marks: joinedData,
      candlestickMarksRaw: nextCandlestickMarksRaw,
    };
  }, [
    propsMarks,
    product,
    marketPriceDriverCombinations,
    tableState.startDate,
    tableState.terms,
    joinMarks,
  ]);

  const candlestickMarks = useMemo(() => {
    if (!tableState.qualityAttributeValueHash) {
      return candlestickMarksRaw;
    }

    return candlestickMarksRaw.filter(
      (mark) =>
        mark.qualityAttributeValueHash === tableState.qualityAttributeValueHash
    );
  }, [candlestickMarksRaw, tableState.qualityAttributeValueHash]);

  const candlestickBounds = useMemo(
    () => getCandlestickBounds(candlestickMarksRaw),
    [candlestickMarksRaw]
  );

  useEffect(() => {
    if (!product || !termScheduleId || !fromDate || !toDate) return;

    const isYearlyHistory =
      historicalReportingPeriodicity === HistoricalReportingPeriodicity.Year;

    if (isYearlyHistory) {
      setTableState((prevState) => ({
        ...prevState,
        qualityAttributeValueHash: null,
        startDate: new Date(fromDate),
        endDate: new Date(toDate),
        terms: buildDailyTermsFromMarks(propsMarks),
        isLoading: false,
      }));
      return;
    }

    const formattedFromDate = fromDate.toLocaleDateString("fr-CA");
    const formattedToDate = toDate.toLocaleDateString("fr-CA");
    const cacheKey = `${termScheduleId}|${formattedFromDate}|${formattedToDate}`;
    const cachedTerms = termsCacheRef.current.get(cacheKey);

    termsRequestIdRef.current += 1;
    const requestId = termsRequestIdRef.current;

    setTableState((prevState) => ({
      ...prevState,
      qualityAttributeValueHash: null,
      startDate: new Date(fromDate),
      endDate: new Date(toDate),
      terms: cachedTerms ?? [],
      isLoading: !cachedTerms,
    }));

    if (cachedTerms) {
      return;
    }

    const initializeTable = async () => {
      try {
        const terms = await getSpotByTermSchedule(
          termScheduleId,
          formattedFromDate,
          formattedToDate
        );

        if (termsRequestIdRef.current !== requestId) {
          return;
        }

        termsCacheRef.current.set(cacheKey, terms);
        setTableState((prevState) => ({
          ...prevState,
          startDate: new Date(fromDate),
          endDate: new Date(toDate),
          terms,
          isLoading: false,
        }));
      } catch (error) {
        console.error("Error initializing table:", error);

        if (termsRequestIdRef.current !== requestId) {
          return;
        }

        setTableState((prevState) => ({
          ...prevState,
          terms: [],
          isLoading: false,
        }));
      }
    };

    initializeTable();
  }, [
    product,
    propsMarks,
    fromDate,
    toDate,
    termScheduleId,
    historicalReportingPeriodicity,
  ]);

  if (tableState.isLoading) return <Loading />;

  return (
    <div className="container-fluid market-chart-panel">
      <div className="row market-data-graph">
        <CandlestickChart
          data={candlestickMarks}
          qualityAttributes={product?.qualityAttributes}
          historicalReportingPeriodicity={historicalReportingPeriodicity}
          startDate={tableState.startDate}
          endDate={tableState.endDate}
          yMin={candlestickBounds.yMin}
          yMax={candlestickBounds.yMax}
          currencySymbol={currencySymbol}
          pricePrecision={pricePrecision}
        />
      </div>
      <div className="row marks-table">
        <ThemeableAgGrid
          className="table-condensed"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoSizeStrategy={{
            type: "fitCellContents",
            skipHeader: false,
            defaultMinWidth: 140,
          }}
          rowData={marks}
          onCellMouseOver={(event) => rowHover(event.data)}
          onCellMouseOut={() => rowExit()}
          onFirstDataRendered={autoSizeGridColumns}
          onRowDataUpdated={autoSizeGridColumns}
          onGridSizeChanged={autoSizeGridColumns}
          getRowId={(params) => params.data.qualityAttributeValueHash}
          domLayout="autoHeight"
          animateRows={true}
          allowHorizontalScroll={true}
        />
      </div>
    </div>
  );
};

export default MarksHistoryTable;
